import { Action, createReducer, on } from '@ngrx/store';
import * as PatientActions from './patient.actions';
import { PatientState } from '../models/patientState';
import { Languages } from 'Shared/app/constants/languages.constants';

const initialState: PatientState = {
  session: null,
  bookingRequest: null,
  activeForm: null,
  language: {
    current: Languages.Swedish.code,
    supportedLanguages: [
      {
        id: '',
        name: Languages.Swedish.name,
        languageName: Languages.Swedish.careproviderName,
        code: Languages.Swedish.code,
        resource: Languages.Swedish.resource,
        country: Languages.Swedish.country,
        rtl: Languages.Swedish.rtl,
        careproviderName: Languages.Swedish.careproviderName
      }
    ]
  },
  unauthorized: false,
  redo: false,
  displayResultPage: false
};

const reducer = createReducer<PatientState>(
  initialState,
  on(PatientActions.setSession, (state, action): PatientState => {
    return {
      ...state,
      session: action.session
    };
  }),
  on(PatientActions.login, (state): PatientState => {
    return {
      ...state,
      unauthorized: false
    };
  }),
  on(PatientActions.loginSuccess, (state, action): PatientState => {
    return {
      ...state,
      session: action.session,
      unauthorized: false
    };
  }),
  on(PatientActions.loginFailure, (state): PatientState => {
    return {
      ...state,
      unauthorized: true
    };
  }),
  on(PatientActions.loadSessionSuccess, (state, action): PatientState => {
    return {
      ...state,
      session: action.session,
      language: {
        ...state.language,
        current: action.session.languageId ? action.session.languageId : state.language.current
      },
      unauthorized: false
    };
  }),
  on(PatientActions.loadBookingRequestSuccess, (state, action): PatientState => {
    return {
      ...state,
      bookingRequest: action.bookingRequest
    };
  }),
  on(PatientActions.logout, (state): PatientState => {
    return {
      ...initialState,
      language: state.language,
      unauthorized: false
    };
  }),
  on(PatientActions.logoutSso, (state): PatientState => {
    return {
      ...initialState,
      language: state.language,
      unauthorized: false
    };
  }),
  on(PatientActions.setActiveForm, (state, action): PatientState => {
    return {
      ...state,
      activeForm: action.id
    };
  }),
  on(PatientActions.clearActiveForm, (state): PatientState => {
    return {
      ...state,
      activeForm: initialState.activeForm,
      redo: initialState.redo,
      displayResultPage: initialState.displayResultPage
    };
  }),
  on(PatientActions.setActiveFormDraft, (state): PatientState => {
    if (state.activeForm) {
      return {
        ...state,
        bookingRequest: {
          ...state.bookingRequest,
          forms: state.bookingRequest.forms.map((f) => (f.id === state.activeForm ? { ...f, status: 20 } : f))
        },
        redo: initialState.redo
      };
    } else {
      return {
        ...state
      };
    }
  }),
  on(PatientActions.setActiveFormCompleted, (state): PatientState => {
    if (state.activeForm) {
      return {
        ...state,
        bookingRequest: {
          ...state.bookingRequest,
          forms: state.bookingRequest.forms.map((f) => (f.id === state.activeForm ? { ...f, status: 30 } : f))
        },
        redo: initialState.redo
      };
    } else {
      return {
        ...state
      };
    }
  }),
  on(PatientActions.loadSupportedLanguagesSuccess, (state, action): PatientState => {
    return {
      ...state,
      language: {
        ...state.language,
        supportedLanguages: action.supportedLanguages
      }
    };
  }),
  on(PatientActions.loadBookingRequestFailure, (state): PatientState => {
    return {
      ...state,
      language: {
        ...state.language,
        supportedLanguages: initialState.language.supportedLanguages
      }
    };
  }),
  on(PatientActions.setCurrentLanguage, (state, action): PatientState => {
    return {
      ...state,
      language: {
        ...state.language,
        current: action.language
      }
    };
  }),
  on(PatientActions.logoutSuccess, (state): PatientState => {
    return {
      ...state,
      session: initialState.session,
      bookingRequest: initialState.bookingRequest,
      activeForm: initialState.activeForm,
      unauthorized: initialState.unauthorized,
      language: {
        ...state.language,
        current: initialState.language.current
      }
    };
  }),
  on(PatientActions.logoutFailure, (state): PatientState => {
    return {
      ...state,
      session: initialState.session,
      bookingRequest: initialState.bookingRequest,
      activeForm: initialState.activeForm,
      unauthorized: initialState.unauthorized,
      language: {
        ...state.language,
        current: initialState.language.current
      }
    };
  }),
  on(PatientActions.setIsRedo, (state): PatientState => {
    return {
      ...state,
      redo: true
    };
  }),
  on(PatientActions.toogleResultPage, (state): PatientState => {
    return {
      ...state,
      displayResultPage: !state.displayResultPage
    };
  })
);

export const patientFeatureKey = 'patient';

export function patientReducer(state, action: Action) {
  return reducer(state, action);
}
